<template>
  <div class="rule-group-card">
    <div class="group-head">
      <el-tag class="group-name">{{ groupTitle }}{{ index + 1 }}</el-tag>
      <div class="group-actions">
        <el-switch
          class="switch"
          v-model="group.status"
          active-text="启用"
          inactive-text="禁用"
        ></el-switch>
        <el-button
          v-if="!readonly"
          class="group-delete"
          type="danger"
          size="small"
          @click="$emit('remove')"
          >删除</el-button
        >
      </div>
    </div>

    <el-form-item
      v-if="isManual"
      class="tag-name-item"
      label="标签名称"
      :prop="'ruleGroupList.' + index + '.tagName'"
      :rules="tagNameRules"
    >
      <el-input
        v-model="group.tagName"
        style="width: 290px"
        size="medium"
        placeholder="请输入"
        maxlength="10"
      ></el-input>
    </el-form-item>

    <!-- 关联策略标签宽度需与父表单一致；其他类型不占标签列。 -->
    <el-form-item
      ref="strategyFormItem"
      class="strategy-form-item"
      label=""
      :label-width="isManual ? '130px' : '0px'"
      :prop="'ruleGroupList.' + index + '.strategyConfig'"
      :rules="strategyRules"
    >
      <template v-if="isManual" slot="label">
        <el-tooltip
          placement="top"
          popper-class="assoc-strategy-tip"
          :content="assocStrategyTip"
        >
          <i class="el-icon-info assoc-tip-icon"></i>
        </el-tooltip>
        关联策略
      </template>
      <div v-if="showEmptyState" class="group-empty">
        <p class="empty-title">当前组内尚未配置规则</p>
        <p class="empty-tip">
          该规则组当前为空，请至少添加一条规则触发限制分支
        </p>
        <el-button
          v-if="!readonly"
          type="primary"
          size="small"
          icon="el-icon-plus"
          @click="$emit('add-strategy')"
          >新增规则</el-button
        >
      </div>

      <template v-else>
        <div
          v-for="(row, i) in group.strategyConfig"
          :key="row.strategyCode"
          class="strategy-row"
          :class="{ 'is-missing': missingRowIndex === i }"
        >
          <span class="strategy-name">{{ row.strategyName }}</span>

          <el-select
            v-if="hasOperatorOf(row)"
            class="operator-select"
            v-model="row.operator"
            size="medium"
            placeholder="请选择"
          >
            <el-option
              v-for="op in operatorOptionsOf(row)"
              :key="op.value"
              :label="op.label"
              :value="op.value"
            ></el-option>
          </el-select>

          <template v-if="row.elType == 1">
            <template v-if="row.operator === 'between'">
              <el-input
                class="value-input"
                :value="hitAt(row, 0)"
                @input="(val) => setHit(row, 0, val)"
                size="medium"
                placeholder="请输入"
              ></el-input>
              <span class="between-sep">到</span>
              <el-input
                class="value-input"
                :value="hitAt(row, 1)"
                @input="(val) => setHit(row, 1, val)"
                size="medium"
                placeholder="请输入"
              ></el-input>
            </template>
            <el-input
              v-else
              class="value-input"
              :value="hitAt(row, 0)"
              @input="(val) => setHit(row, 0, val)"
              size="medium"
              placeholder="请输入"
            ></el-input>
          </template>
          <el-checkbox-group
            v-else-if="row.elType == 2"
            class="value-checkbox-group"
            v-model="row.hits"
          >
            <el-checkbox
              v-for="opt in row.elData || []"
              :key="opt.value"
              :label="opt.value"
              >{{ opt.label }}</el-checkbox
            >
          </el-checkbox-group>
          <!-- 门店商品组件只更新 elData，提交时由父页面转换为商品 id。 -->
          <StoreGoodsDisplay
            v-else-if="row.elType == 5"
            :hits="row.hits"
            v-model="row.elData"
            :showEdit="!readonly"
            edit-as-text
            @change="(options) => onStoreGoodsChange(row, options)"
          />
          <household-editor v-else-if="row.elType == 6" :row="row" :readonly="readonly" />
          <span
            v-else-if="row.elType && row.elType != 3 && row.elType != 4"
            class="value-unsupported"
            >该策略的值控件待确认（elType {{ row.elType }}）</span
          >

          <el-button
            v-if="!readonly"
            class="row-delete"
            type="text"
            icon="el-icon-close"
            @click="removeStrategy(i)"
          ></el-button>
        </div>

        <div
          class="row-actions"
          :class="{ 'row-actions--flush': isManual && !group.strategyConfig.length }"
        >
          <el-button
            v-if="!readonly"
            type="primary"
            size="small"
            icon="el-icon-plus"
            @click="$emit('add-strategy')"
            >添加</el-button
          >
        </div>
      </template>
    </el-form-item>

    <div class="group-divider"></div>

    <el-form-item
      v-if="hasAdjust"
      class="adjust-item"
      label="首付比浮动方向"
      :prop="'ruleGroupList.' + index + '.adjustDirection'"
      :rules="adjustDirectionRules"
    >
      <el-radio-group v-model="group.adjustDirection">
        <el-radio :label="1">上调首付比</el-radio>
        <el-radio :label="-1">下调首付比</el-radio>
      </el-radio-group>
    </el-form-item>

    <el-form-item
      v-if="hasAdjust"
      ref="adjustValueFormItem"
      class="adjust-item"
      label="首付比浮动值"
      :prop="'ruleGroupList.' + index + '.adjustValue'"
      :rules="adjustValueRules"
    >
      <div class="input-with-suffix">
        <el-input-number
          v-model="group.adjustValue"
          :controls="false"
          :min="1"
          :max="20"
          :precision="0"
          placeholder="请输入"
          size="small"
          @change="validateAdjustValue"
          @blur="validateAdjustValue"
        />
        <span class="suffix">%</span>
      </div>
    </el-form-item>

    <el-form-item
      class="hit-remark-item"
      :class="{ 'hit-remark-item--aligned': alignHitRemark }"
      label="命中建议"
    >
      <el-input
        v-model="group.hitRemark"
        style="width: 670px"
        type="textarea"
        :rows="3"
        size="medium"
        placeholder="请输入"
      ></el-input>
    </el-form-item>
  </div>
</template>

<script>
import BaseStoreGoodsDisplay from "@/views/review/review-rule/StoreGoodsDisplay.vue";
import HouseholdEditor from "./household-editor.vue";
import { getStrategyOperatorList } from "./strategy-operator";
import {
  findStrategyError,
  needsStrategyOperator,
} from "./strategy-validation";
import {
  EnumMachineRuleType,
  hasAdjustFields,
} from "@/enum/dict/machine-rule.js";

// 仅机审 2.0 额外通知主动选择，区分清空商品与异步回显。
const StoreGoodsDisplay = {
  extends: BaseStoreGoodsDisplay,
  methods: {
    // 保存时同步商品 ID，主动清空后不回退到旧 hits。
    async saveStoreGoods() {
      await BaseStoreGoodsDisplay.methods.saveStoreGoods.call(this);
      this.$emit("change", this.selectedStoreGoods);
    },
  },
};

export default {
  name: "RuleGroupCard",

  components: { StoreGoodsDisplay, HouseholdEditor },

  props: {
    group: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    ruleType: {
      type: Number,
      default: undefined,
    },
    // 字段继承父表单禁用状态，动作按钮及子组件入口需额外隐藏。
    readonly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      assocStrategyTip:
        "关联策略是「标签名称」的附加条件。标签名称和这里配置的全部关联策略，共同组成一个条件组。",
      missingRowIndex: -1,
      strategyValidated: false,
    };
  },

  computed: {
    alignHitRemark() {
      return [
        EnumMachineRuleType.BASE,
        EnumMachineRuleType.REPURCHASE,
        EnumMachineRuleType.REJECT,
      ].includes(this.ruleType);
    },
    isManual() {
      return this.ruleType === EnumMachineRuleType.MANUAL;
    },
    hasAdjust() {
      return hasAdjustFields(this.ruleType);
    },
    groupTitle() {
      return this.isManual ? "标签组" : "规则组";
    },
    showEmptyState() {
      return !this.isManual && !this.group.strategyConfig.length;
    },
    // 记录首个错误行，仅该行显示红边。
    strategyRules() {
      const requireOne = !this.isManual;
      return [
        {
          validator: (rule, value, callback) => {
            this.strategyValidated = true;
            const err = findStrategyError(value || [], requireOne);
            this.missingRowIndex = err ? err.index : -1;
            callback(err ? new Error(err.message) : undefined);
          },
          // 保存时统一校验，之后由监听器随修改重新校验。
          trigger: "submit",
        },
      ];
    },
    tagNameRules() {
      return [
        {
          required: true,
          whitespace: true,
          message: "请输入标签名称",
          trigger: "blur",
        },
      ];
    },
    adjustDirectionRules() {
      return [
        { required: true, message: "请选择首付比浮动方向", trigger: "change" },
      ];
    },
    adjustValueRules() {
      return [
        {
          required: true,
          message: "请输入首付比浮动值",
          trigger: ["change", "blur"],
        },
        {
          type: "integer",
          min: 1,
          max: 20,
          message: "首付比浮动值超出范围，请填写 1 到 20% 之间的数值",
          trigger: ["change", "blur"],
        },
      ];
    },
  },

  watch: {
    // 首次保存校验后，策略修改时重新校验而非直接清除错误。
    "group.strategyConfig": {
      handler() {
        this.$nextTick(() => {
          if (this.strategyValidated && this.$refs.strategyFormItem) {
            this.$refs.strategyFormItem.validate("", () => {});
          }
        });
      },
      deep: true,
    },
  },

  methods: {
    // 数值组件提交输入后，等待绑定值更新再刷新校验结果。
    validateAdjustValue() {
      this.$nextTick(() => {
        const field = this.$refs.adjustValueFormItem;
        if (field) field.validate("", () => {});
      });
    },
    hasOperatorOf(row) {
      return needsStrategyOperator(row.elType) && Number(row.elType) !== 6;
    },
    operatorOptionsOf(row) {
      return getStrategyOperatorList(row);
    },
    hitAt(row, i) {
      return row.hits[i];
    },
    // Vue 2 数组下标更新使用 $set。
    setHit(row, i, val) {
      this.$set(row.hits, i, val);
    },
    // 主动选择商品时同步提交值，空数组表示明确清空。
    onStoreGoodsChange(row, options) {
      row.hits = options.map((item) => item.value);
    },
    removeStrategy(i) {
      this.group.strategyConfig.splice(i, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.rule-group-card {
  width: 1000px;
  margin-top: 45px;
  padding: 27px 35px;
  background: rgba(255, 255, 255, 1);
  border-radius: 6px;
  border: 1px solid #dcdfe6;

  .group-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0 -35px 15px;
    padding: 0 35px 10px;
    border-bottom: 1px solid #dcdfe6;
  }

  .group-actions {
    display: flex;
    align-items: center;

    .switch {
      margin-right: 12px;
    }
  }

  .group-delete {
    font-size: 14px;
    background-color: #e64622;
    border-color: #e64622;

    &:hover,
    &:focus {
      background-color: #eb6b4e;
      border-color: #eb6b4e;
    }

    &:active {
      background-color: #b8381b;
      border-color: #b8381b;
    }
  }

  .group-empty {
    padding: 24px 0;
    background: #f9fafb;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;

    .empty-title {
      margin: 0;
      color: #303133;
    }

    .empty-tip {
      margin: 6px 0 14px;
      color: #909399;
    }
  }

  .strategy-row {
    display: flex;
    align-items: center;
    padding: 8px 24px;
    background: #f9fafb;

    & + & {
      border-top: 1px solid #ebeef5;
    }

    .strategy-name {
      width: 240px;
      color: #303133;
      font-weight: 600;
    }

    .operator-select {
      width: 130px;
      margin-right: 15px;
    }

    .value-input {
      width: 130px;
      margin-right: 15px;
    }

    .between-sep {
      margin: 0 15px;
    }

    .value-checkbox-group {
      flex: 1;
      min-width: 0;
      margin-right: 15px;
    }

    .value-unsupported {
      color: #909399;
    }

    .store-goods-display + .row-delete,
    .household-editor + .row-delete {
      margin-left: 10px;
      flex-shrink: 0;
    }

    .row-delete {
      margin-left: auto;
      padding: 0;
      color: #909399;

      &:hover,
      &:focus {
        color: #f56c6c;
      }
    }

    &.is-missing {
      .strategy-name {
        color: #f56c6c;
      }

      /deep/ .operator-select .el-input__inner,
      /deep/ .value-input .el-input__inner {
        border-color: #f56c6c;
      }
    }
  }

  .row-actions {
    margin: 19px 0 0;

    &--flush {
      margin-top: 0;
    }
  }

  .assoc-tip-icon {
    margin-right: 4px;
    color: #909399;
    cursor: pointer;
  }

  .tag-name-item {
    margin-bottom: 22px;
  }

  .adjust-item {
    margin-top: 22px;
  }

  // 覆盖父页面的 18px 行高，使单选内容与标签垂直对齐。
  .adjust-item /deep/ .el-form-item__content {
    line-height: 40px;
  }

  .input-with-suffix {
    display: flex;
    align-items: center;
    width: 190px;

    .el-input-number {
      flex: 1;
      min-width: 0;
      width: auto;
    }

    .suffix {
      flex: 0 0 auto;
      margin-left: 8px;
      color: #909399;
      white-space: nowrap;
    }
  }

  .group-divider {
    margin: 22px -35px 0;
    border-top: 1px solid #dcdfe6;
  }

  /deep/ .store-goods-display {
    .el-dialog__body {
      padding-top: 0; // v1 同（Element 默认 30px）
    }

    .el-table {
      overflow-y: scroll; // v1 同：表格恒定留着滚动条位置

      &::before {
        display: none; // 去掉 Element 表格自己画的底部 1px 线
      }

      .el-table__cell {
        padding: 8px 0; // v1：12px → 8px
      }

      .el-table__header th {
        background-color: #f5f5f5;
        color: #1a1e25;
      }
    }

    .store-goods-content .search-section .el-form {
      display: flex;
      align-items: center;

      .el-form-item {
        display: flex;
        align-items: center;
        margin-bottom: 0;
      }

      .el-form-item__label {
        line-height: 32px;
        padding-bottom: 0;
      }

      .el-form-item__content {
        display: flex;
        align-items: center;
        line-height: 32px;
      }
    }
  }

  /deep/ .strategy-form-item {
    margin-bottom: 0;

    &.is-error .el-input__inner {
      border-color: #dcdfe6;

      &:hover {
        border-color: #c0c4cc;
      }

      &:focus {
        border-color: #409eff;
      }
    }
  }

  /deep/ .hit-remark-item {
    margin-top: 25px;
    margin-bottom: 18px;

    &--aligned .el-form-item__label {
      padding-left: 24px;
      text-align: left;
      color: #303133;
      font-weight: 600;
    }
  }
}
</style>

<style lang="scss" scoped>
.rule-group-card /deep/ {
  .switch.el-switch {
    height: 31px;
    line-height: 31px;
  }

  .switch .el-switch__core {
    width: 69px !important;
    height: 31px;
    border-radius: 16px;
    margin: 0; // 原来那句 `width: 55px !important; margin: 0` 的 margin 部分
  }

  .switch .el-switch__core:after {
    width: 27px;
    height: 27px;
  }

  .switch.is-checked .el-switch__core::after {
    left: 100%;
    margin-left: -28px;
  }

  .switch .el-switch__label {
    position: absolute;
    display: none;
    width: 69px;
    height: 31px;
    margin: 0;
    color: #fff !important;
  }

  .switch .el-switch__label--right {
    z-index: 1;
  }

  .switch .el-switch__label--right span {
    margin-left: 12px;
  }

  .switch .el-switch__label--left {
    z-index: 1;
  }

  .switch .el-switch__label--left span {
    margin-left: 32px;
  }

  .switch .el-switch__label.is-active {
    display: block;
  }
}
</style>

<!-- Tooltip 浮层挂在 body，使用独立类名限定非 scoped 样式。 -->
<style lang="scss">
.assoc-strategy-tip.el-tooltip__popper {
  max-width: 283px;
  line-height: 18px;
}
</style>
